"use client";

import { useEffect, useRef, useState } from "react";
import type { AiMarkerKind } from "@repo/ai-disclaimer";
import {
  AI_ICON_VARIANTS,
  resolveAiIcon,
  isPlaceholderAiIcon,
  bindingsToIconSet,
  type AiIconBindings as Bindings,
  type AiIconVariant,
} from "@repo/utils/aiDisclaimerIcons";
import type { InventoryRow } from "@repo/utils/aiImagesInventory";
import {
  AdminButton,
  AdminInput,
  AdminNotice,
  AdminPanel,
  AdminSelect,
} from "./AdminUi";

const KIND_LABEL: Record<AiMarkerKind, string> = {
  generated: "AI-generated",
  modified: "AI-edited",
  unspecified: "AI (unspecified)",
};

const VARIANT_HINT: Record<AiIconVariant, string> = {
  // The variant names the image the badge sits ON, not the badge's own colour. Spelled out
  // wherever the fields appear, because binding the pair the wrong way round is invisible
  // until it ships: a white badge simply vanishes against a light photo.
  light: "for light images",
  dark: "for dark images",
};

/**
 * Icon binding for the marker kind currently selected in the toolbar.
 *
 * Collapsed to two swatches and a button rather than a section listing all three kinds:
 * the toolbar already names the kind being worked on, so repeating every kind's fields
 * costs a third of the page to show two rows nobody is editing.
 */
/** Filename for a bound url, or a plain word — a confirmation must read at a glance. */
function describe(url?: string): string {
  if (!url) return "placeholder";

  return url.split("/").pop() || url;
}

const AiIconBindings = ({
  kind,
  bindings,
  assets,
  onSaved,
}: {
  kind: AiMarkerKind;
  bindings: Bindings;
  /** Image assets in this space — the picker's options; any url is still valid. */
  assets: InventoryRow[];
  onSaved: (next: Bindings) => void;
}) => {
  const [draft, setDraft] = useState<Bindings>(bindings);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The inventory reload after a save returns the stored bindings; adopt them so the
  // swatches show what is actually persisted rather than what was typed.
  useEffect(() => {
    setDraft(bindings);
  }, [bindings]);

  useEffect(() => {
    if (!open) return;

    const onDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const iconSet = bindingsToIconSet(draft);

  const dirty = AI_ICON_VARIANTS.some(
    (variant) =>
      (draft[kind][variant]?.filename ?? "") !==
      (bindings[kind][variant]?.filename ?? ""),
  );

  const setUrl = (variant: AiIconVariant, url: string, id = 0) => {
    setDraft((current) => ({
      ...current,
      [kind]: {
        ...current[kind],
        [variant]: url.trim() ? { id, filename: url.trim() } : null,
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);

    try {
      const response = await fetch("/api/ai-images/icons", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          light: draft[kind].light?.filename ?? null,
          dark: draft[kind].dark?.filename ?? null,
        }),
      });

      const body = (await response.json()) as {
        icons?: Bindings;
        error?: string;
        detail?: string;
      };

      if (!response.ok) {
        setNotice(
          body.detail ?? body.error ?? `Save failed (${response.status})`,
        );
        return;
      }

      if (body.icons) {
        setDraft(body.icons);
        onSaved(body.icons);
      }

      // Stays open, with a confirmation naming what was stored. Closing on success left
      // nothing on screen to distinguish "saved" from "the click did nothing".
      setNotice(
        `Saved. ${KIND_LABEL[kind]}: light ${describe(
          body.icons?.[kind]?.light?.filename,
        )}, dark ${describe(body.icons?.[kind]?.dark?.filename)}.`,
      );
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={panelRef} className="relative flex items-end gap-2 text-sm">
      <span className="mr-1 text-[var(--admin-text-muted)]">Icons</span>

      {AI_ICON_VARIANTS.map((variant) => {
        const src = resolveAiIcon(kind, variant, iconSet);

        return (
          <span
            key={variant}
            title={`${variant} (${VARIANT_HINT[variant]}) — ${
              isPlaceholderAiIcon(src) ? "placeholder" : "bound"
            }`}
            className={`flex h-8 w-20 items-center justify-center rounded-[var(--admin-radius)] border px-1 ${
              variant === "light"
                ? "border-[var(--admin-border)] bg-[var(--admin-surface-muted)]"
                : "border-[var(--admin-text)] bg-[var(--admin-text)]"
            }`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={`${KIND_LABEL[kind]} badge, ${variant}`}
              className={`max-h-6 max-w-full ${
                isPlaceholderAiIcon(src) ? "opacity-60" : ""
              }`}
            />
          </span>
        );
      })}

      <AdminButton
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        {dirty ? "Link icons… •" : "Link icons…"}
      </AdminButton>

      {open && (
        <AdminPanel className="absolute top-full right-0 z-20 mt-2 w-[28rem] shadow-lg">
          <p className="mb-3 text-sm font-medium">
            Badge icons for {KIND_LABEL[kind]}
          </p>

          {AI_ICON_VARIANTS.map((variant) => (
            <div key={variant} className="mb-3 flex flex-col gap-1">
              <span className="text-xs capitalize">
                {variant}{" "}
                <span className="text-[var(--admin-text-muted)]">
                  ({VARIANT_HINT[variant]})
                </span>
              </span>
              <AdminInput
                value={draft[kind][variant]?.filename ?? ""}
                onChange={(event) => setUrl(variant, event.target.value)}
                placeholder="https://… — any host, or empty for the placeholder"
                className="font-mono text-xs"
                aria-label={`${KIND_LABEL[kind]} ${variant} icon url`}
              />
              {/* Reflects the field above rather than resetting to the placeholder
                  option: a picker that snaps back after every choice reads as the choice
                  not having registered, which is exactly how this looked in use. An
                  externally hosted url has no matching option, so the select falls back to
                  the "pasted url" option and the field remains the source of truth. */}
              <AdminSelect
                // Matched by url, not by the stored id: a binding read back from the CMS
                // is a bare url string and carries no asset id, so keying off the id would
                // leave the picker blank for every icon that was actually saved.
                value={String(
                  assets.find(
                    (row) => row.url === draft[kind][variant]?.filename,
                  )?.id ?? "",
                )}
                onChange={(event) => {
                  const asset = assets.find(
                    (row) => row.id === Number(event.target.value),
                  );

                  if (asset) setUrl(variant, asset.url, asset.id);
                }}
                className="text-xs"
                aria-label={`Pick a ${variant} icon from this space`}
              >
                <option value="">
                  {draft[kind][variant]
                    ? "— pasted url —"
                    : "— or pick from this space —"}
                </option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.filename || `asset ${asset.id}`}
                  </option>
                ))}
              </AdminSelect>
            </div>
          ))}

          {notice && (
            <AdminNotice tone="info" className="mb-2 p-2 text-xs">
              {notice}
            </AdminNotice>
          )}

          <div className="flex items-center gap-2">
            <AdminButton
              variant="primary"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? "Saving…" : dirty ? "Save changes" : "Save"}
            </AdminButton>
            <AdminButton
              onClick={() => {
                setUrl("light", "");
                setUrl("dark", "");
              }}
            >
              Clear both
            </AdminButton>
            <span className="ml-auto text-xs text-[var(--admin-text-muted)]">
              {dirty
                ? "Unsaved changes — nothing is stored until you save."
                : "Bind one or both — an empty field falls back to the placeholder."}
            </span>
          </div>
        </AdminPanel>
      )}
    </div>
  );
};

export default AiIconBindings;
